import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '../ui/select';
import { X, Plus, Trash2, Search, Star } from 'lucide-react';
import { useMemo, useState } from 'react';

const HAS_OPTIONS = ['dropdown', 'multi_select', 'radio'];
const IS_STATIC = ['heading', 'paragraph', 'divider', 'page_break'];

const CONDITION_OPS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

/**
 * Right-pane editor for a single form field. All edits are emitted via
 * `onChange(patch)` so the parent owns the field list.
 */
export const FormFieldEditor = ({ field, allFields, bullhornFields, onChange, onClose }) => {
  const isStatic = IS_STATIC.includes(field.field_type);
  const hasOptions = HAS_OPTIONS.includes(field.field_type);

  const addOption = () => {
    const next = (field.options?.length || 0) + 1;
    onChange({
      options: [...(field.options || []), { value: `option${next}`, label: `Option ${next}` }],
    });
  };

  const updateOption = (idx, patch) => {
    const arr = [...(field.options || [])];
    arr[idx] = { ...arr[idx], ...patch };
    onChange({ options: arr });
  };

  const removeOption = (idx) => {
    const arr = (field.options || []).filter((_, i) => i !== idx);
    onChange({ options: arr });
  };

  const setCondition = (patch) => {
    const current = field.condition || {};
    const next = { ...current, ...patch };
    // Strip empty conditions back to null so the public flow doesn't
    // misinterpret an empty rule as "always hide".
    if (!next.field_id && !next.operator) {
      onChange({ condition: null });
    } else {
      onChange({ condition: next });
    }
  };

  return (
    <div className="p-5 space-y-5" data-testid={`form-field-editor-${field.id}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base">Field Properties</h3>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="form-field-editor-close-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Static blocks: heading / paragraph / divider / page_break */}
      {isStatic ? (
        field.field_type === 'divider' || field.field_type === 'page_break' ? (
          <p className="text-sm text-muted-foreground italic">
            {field.field_type === 'divider'
              ? 'A divider has no editable content — it just renders a horizontal rule.'
              : 'A page break splits the form into multiple pages at this position. Candidates will navigate page-by-page with a progress bar.'}
          </p>
        ) : (
          <div>
            <Label className="text-xs">{field.field_type === 'heading' ? 'Heading Text' : 'Paragraph Text'}</Label>
            <Textarea
              rows={field.field_type === 'heading' ? 1 : 4}
              value={field.label || ''}
              onChange={(e) => onChange({ label: e.target.value })}
              data-testid="form-field-editor-static-content-input"
            />
          </div>
        )
      ) : (
        <>
          <div>
            <Label className="text-xs">Label</Label>
            <Input
              value={field.label || ''}
              onChange={(e) => onChange({ label: e.target.value })}
              data-testid="form-field-editor-label-input"
            />
          </div>
          <div>
            <Label className="text-xs">Placeholder</Label>
            <Input
              value={field.placeholder || ''}
              onChange={(e) => onChange({ placeholder: e.target.value })}
              data-testid="form-field-editor-placeholder-input"
            />
          </div>
          <div>
            <Label className="text-xs">Help text</Label>
            <Input
              value={field.help_text || ''}
              onChange={(e) => onChange({ help_text: e.target.value })}
              data-testid="form-field-editor-help-input"
            />
          </div>

          <div className="flex items-center justify-between p-3 border border-border rounded-lg">
            <div>
              <p className="text-sm font-medium">Required</p>
              <p className="text-xs text-muted-foreground">Candidate must fill before submitting</p>
            </div>
            <Switch
              checked={!!field.required}
              onCheckedChange={(v) => onChange({ required: v })}
              data-testid="form-field-editor-required-toggle"
            />
          </div>

          {/* Options for dropdown/radio/multi-select */}
          {hasOptions && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Options</Label>
                <Button size="sm" variant="ghost" className="gap-1 h-7" onClick={addOption} data-testid="form-field-editor-add-option-btn">
                  <Plus className="w-3 h-3" /> Add
                </Button>
              </div>
              <div className="space-y-1.5">
                {(field.options || []).map((opt, i) => (
                  <div key={opt.value || `opt-${i}`} className="flex gap-1.5 items-center">
                    <Input
                      placeholder="Label"
                      value={opt.label}
                      onChange={(e) => updateOption(i, { label: e.target.value })}
                      className="text-sm"
                      data-testid={`form-field-editor-option-label-${i}`}
                    />
                    <Input
                      placeholder="value"
                      value={opt.value}
                      onChange={(e) => updateOption(i, { value: e.target.value })}
                      className="text-sm w-24"
                      data-testid={`form-field-editor-option-value-${i}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-600 hover:bg-red-50"
                      onClick={() => removeOption(i)}
                      data-testid={`form-field-editor-option-remove-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bullhorn mapping */}
          <div>
            <Label className="text-xs">Map to Bullhorn field</Label>
            <BullhornMappingSelect
              value={field.bullhorn_source_field || ''}
              onChange={(v) => onChange({ bullhorn_source_field: v || null })}
              bullhornFields={bullhornFields}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Optional. When set, the existing Bullhorn value pre-fills this field
              and the candidate's answer writes back on submit.
            </p>

            {/* Direction toggles — only meaningful when a mapping is set. */}
            {field.bullhorn_source_field && (
              <div className="mt-3 grid grid-cols-1 gap-2">
                <label
                  className="flex items-center justify-between p-2.5 border border-border rounded-lg cursor-pointer hover:bg-slate-50"
                  data-testid="form-field-editor-prefill-toggle-row"
                >
                  <div className="pr-2">
                    <p className="text-sm font-medium">Pre-fill from Bullhorn</p>
                    <p className="text-xs text-muted-foreground">
                      Show the candidate the existing value when they open the form.
                    </p>
                  </div>
                  <Switch
                    checked={field.prefill_from_bullhorn !== false}
                    onCheckedChange={(v) => onChange({ prefill_from_bullhorn: v })}
                    data-testid="form-field-editor-prefill-toggle"
                  />
                </label>
                <label
                  className="flex items-center justify-between p-2.5 border border-border rounded-lg cursor-pointer hover:bg-slate-50"
                  data-testid="form-field-editor-writeback-toggle-row"
                >
                  <div className="pr-2">
                    <p className="text-sm font-medium">Write back to Bullhorn</p>
                    <p className="text-xs text-muted-foreground">
                      Push the candidate's answer to the Bullhorn record on submit.
                    </p>
                  </div>
                  <Switch
                    checked={field.write_back_to_bullhorn !== false}
                    onCheckedChange={(v) => onChange({ write_back_to_bullhorn: v })}
                    data-testid="form-field-editor-writeback-toggle"
                  />
                </label>
              </div>
            )}
          </div>

          {/* Conditional logic */}
          <div className="border-t border-border pt-4">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Show this field when…</Label>
            <div className="space-y-2 mt-2">
              <Select
                value={field.condition?.field_id || '__none__'}
                onValueChange={(v) => setCondition({ field_id: v === '__none__' ? null : v })}
              >
                <SelectTrigger data-testid="form-field-editor-condition-field">
                  <SelectValue placeholder="Always show" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Always show</SelectItem>
                  {(allFields || [])
                    .filter((f) => f.id !== field.id && !IS_STATIC.includes(f.field_type))
                    .map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {field.condition?.field_id && (
                <>
                  <Select
                    value={field.condition.operator || 'equals'}
                    onValueChange={(v) => setCondition({ operator: v })}
                  >
                    <SelectTrigger data-testid="form-field-editor-condition-op">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!['is_empty', 'is_not_empty'].includes(field.condition.operator) && (
                    <Input
                      placeholder="Value"
                      value={field.condition.value || ''}
                      onChange={(e) => setCondition({ value: e.target.value })}
                      data-testid="form-field-editor-condition-value"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Frequently-used Bullhorn fields — pinned at the top of the dropdown.
// Mirrors the document-template-editor pattern so recruiters get a
// consistent mapping experience across both surfaces.
const FREQUENTLY_USED = new Set([
  'firstName', 'lastName', 'email', 'phone', 'mobile',
  'address.address1', 'address.city', 'address.state', 'address.zip',
  'dateOfBirth', 'ssn', 'nationalIdNumber',
]);

/**
 * Bullhorn-field picker — categorized + searchable. Renders the field
 * label prominently with the backend field name muted on the right
 * (e.g. "First Name   firstName"), matching the document template
 * editor's mapping dropdown.
 */
function BullhornMappingSelect({ value, onChange, bullhornFields }) {
  const [query, setQuery] = useState('');

  const { frequent, byCategory, categories, totalCount, orphan } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = (bullhornFields || []).map((bf) => ({
      value: bf.name || bf.value,
      label: bf.label || bf.name || bf.value,
      category: bf.category || 'Other',
    })).filter((f) => f.value);

    const matchesQ = (f) =>
      !q || f.label.toLowerCase().includes(q) || f.value.toLowerCase().includes(q);

    const freq = [];
    const cats = {};
    all.forEach((f) => {
      if (!matchesQ(f)) return;
      if (FREQUENTLY_USED.has(f.value)) freq.push(f);
      else {
        if (!cats[f.category]) cats[f.category] = [];
        cats[f.category].push(f);
      }
    });

    // Stable sort: frequently-used by predefined order, categories alpha.
    const byLabel = (a, b) => a.label.localeCompare(b.label);
    freq.sort(byLabel);
    Object.keys(cats).forEach((k) => cats[k].sort(byLabel));
    const cat_keys = Object.keys(cats).sort();

    // Orphan: the currently-mapped value isn't present in the loaded
    // schema (e.g. the tenant's Bullhorn entity lost the custom field
    // or the API timed out before our load completed). Synthesise an
    // entry so the Select can resolve and the recruiter still SEES
    // what was mapped — never a blank dropdown.
    const isPresent = all.some((f) => f.value === value);
    const orphanEntry = value && !isPresent ? { value, label: value, category: 'Unknown' } : null;

    return {
      frequent: freq,
      byCategory: cats,
      categories: cat_keys,
      totalCount: all.length,
      orphan: orphanEntry,
    };
  }, [bullhornFields, query, value]);

  return (
    <Select
      value={value || '__none__'}
      onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
    >
      <SelectTrigger data-testid="form-field-editor-bullhorn-select">
        <SelectValue placeholder="No mapping" />
      </SelectTrigger>
      <SelectContent className="max-h-96">
        {/* Search input */}
        <div className="px-2 pb-2 pt-2 sticky top-0 bg-popover z-10 border-b border-border">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${totalCount} fields…`}
              className="w-full h-8 pl-8 pr-3 text-sm border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              onKeyDown={(e) => {
                // Keep typing inside the search — don't let Radix swallow it.
                e.stopPropagation();
              }}
              data-testid="form-field-editor-bullhorn-search"
            />
          </div>
        </div>

        <SelectItem value="__none__">
          <span className="text-muted-foreground">No mapping</span>
        </SelectItem>

        {orphan && (
          <SelectItem value={orphan.value}>
            <div className="flex items-center justify-between w-full gap-3">
              <span className="text-amber-700">{orphan.label}</span>
              <span className="text-xs text-muted-foreground font-mono">{orphan.value}</span>
            </div>
          </SelectItem>
        )}

        {frequent.length > 0 && (
          <SelectGroup>
            <SelectLabel className="flex items-center gap-1.5 text-xs">
              <Star className="w-3 h-3 text-amber-500" />
              Frequently Used
            </SelectLabel>
            {frequent.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                <div className="flex items-center justify-between w-full gap-3">
                  <span>{f.label}</span>
                  <span className="text-xs text-muted-foreground font-mono">{f.value}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}

        {categories.map((cat) => (
          <SelectGroup key={cat}>
            <SelectLabel className="text-xs">{cat}</SelectLabel>
            {byCategory[cat].map((f) => (
              <SelectItem key={f.value} value={f.value}>
                <div className="flex items-center justify-between w-full gap-3">
                  <span>{f.label}</span>
                  <span className="text-xs text-muted-foreground font-mono">{f.value}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}

        {!frequent.length && !categories.length && (
          <div className="px-2 py-6 text-sm text-muted-foreground text-center">
            {query ? `No fields match "${query}"` : 'No fields loaded'}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
